import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { buildAttachmentUploadPath } from "../utils/attachmentUpload";

import {
  EMPLOYMENT_STATUS,
  PARTS_ACTION_STATUS,
  PARTS_REQUEST_TYPE,
  PARTS_STATUS,
  RO_LIFECYCLE,
  RO_STATUS,
  TRANSPORT_TYPES,
  WORK_MIX,
} from "../constants/roStatus";

import {
  addEvent,
  alignLifecycleAndStatus,
  canonicalizePatch,
  genId,
  getAttentionFlags,
  getLifecycle,
  isArchived,
  isCompletedRO,
  isInProgressRO,
  isOpenAssignedRO,
  isTodayISO,
  isWaitingForAssignment,
  isWaitingForParts,
  isWipRO,
  isoDateKeyInTimeZone,
  normalizeEmploymentStatus,
  normalizeLifecycle,
  normalizePartsActionStatus,
  normalizePartsStatus,
  normalizeRO,
  normalizeStatus,
  normalizeTransportType,
  normalizeVinLast8,
  normalizeWorkMix,
  nowISO,
  safeNumber,
  safeString,
  secBetween,
  stripReadOnlyRoPatchFields,
  validateTagNumberUnique,
} from "../utils/roModel";

const STORAGE_KEY = "flowtrak_ro_store_v5";
const SCHEMA_VERSION = 5;

const DEFAULT_SNAPSHOTS = Object.freeze({});

const DEFAULT_SETTINGS = Object.freeze({
  dealerName: "",
  storeId: "default",
  timezone: "America/Los_Angeles",
  setupCompleted: false,
  staffDirectory: [],
});

const DEFAULT_CURRENT_USER = Object.freeze({
  id: "user_admin",
  name: "Admin",
  role: "admin",
});

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").trim();

function apiUrl(path) {
  const p = String(path || "").replace(/^\/+/, "");

  if (!API_BASE) {
    return `/${p}`;
  }

  const base = API_BASE.replace(/\/+$/, "");
  return `${base}/${p}`;
}

function firstNonBlank(...values) {
  for (const value of values) {
    const s = safeString(value).trim();
    if (s) return s;
  }
  return "";
}

function isNetworkLikeError(e) {
  const msg = safeString(e?.message || e).toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed") ||
    msg.includes("network")
  );
}

function friendlyApiError(e, fallback = "Flowtrak could not complete the request.") {
  const msg = safeString(e?.message || e);

  if (isNetworkLikeError(e)) {
    return "Unable to reach the Flowtrak server. Please check your connection and try again.";
  }

  if (msg.includes("403")) {
    return "Request blocked. Please check permissions or backend CORS settings.";
  }

  if (msg.includes("401")) {
    return "Your session expired. Please sign in again.";
  }

  if (msg.includes("404")) {
    return "Flowtrak could not find the requested API route.";
  }

  if (msg.includes("409") || msg.toLowerCase().includes("already in use")) {
    return "Tag # is already in use on an OPEN RO.";
  }

  if (msg.includes("400") && msg.toLowerCase().includes("vin")) {
    return "VIN (Last 8) is required.";
  }

  if (msg.includes("400") && msg.toLowerCase().includes("missing id")) {
    return "Save failed because the RO id was missing.";
  }

  return fallback;
}

function repairVinAliases(source = {}) {
  const vin = normalizeVinLast8(
    firstNonBlank(
      source?.vinLast8,
      source?.vin_last_8,
      source?.vin_last8,
      source?.vinLast,
      source?.vin
    )
  );

  return {
    ...(source && typeof source === "object" ? source : {}),
    vin,
    vinLast8: vin,
    vin_last_8: vin,
    vin_last8: vin,
    vinLast: vin,
  };
}

function actionKey(value) {
  return safeString(value)
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

function roStatusValue(name, fallback) {
  const v = RO_STATUS?.[name];
  return safeString(v).trim() || fallback;
}

function roLifecycleValue(name, fallback) {
  const v = RO_LIFECYCLE?.[name];
  return safeString(v).trim() || fallback;
}

function partsStatusValue(name, fallback) {
  const v = PARTS_STATUS?.[name];
  return safeString(v).trim() || fallback;
}

function partsActionValue(name, fallback) {
  const v = PARTS_ACTION_STATUS?.[name];
  return safeString(v).trim() || fallback;
}

function isPartsReviewAction(value) {
  const key = actionKey(value);
  return key === "REVIEW" || key === "OPEN" || key === "REOPEN";
}

function inferPartsStatusFromAction(action, requestType, currentPartsStatus) {
  const key = actionKey(action);

  if (key === "NO_ACTION" || key === "NONE" || key === "CLEAR") {
    return partsStatusValue("NONE", "NONE");
  }

  if (key === "PRE_PULLED" || key === "PREPULLED" || key === "PULLED") {
    return partsStatusValue("PULLED", "PULLED");
  }

  if (key === "ORDERED") {
    return partsStatusValue("ORDERED", "ORDERED");
  }

  if (key === "ARRIVED") {
    return partsStatusValue("ARRIVED", "ARRIVED");
  }

  if (key === "QUOTED" || key === "QUOTE_PROVIDED") {
    return partsStatusValue("QUOTED", "QUOTED");
  }

  if (isPartsReviewAction(action)) {
    return partsStatusValue("NONE", "NONE");
  }

  return currentPartsStatus || partsStatusValue("NONE", "NONE");
}

function getAttentionFlagList(ro) {
  const direct =
    ro?.attentionFlags ??
    ro?.attention_flags ??
    ro?.flags ??
    [];

  if (Array.isArray(direct)) {
    return direct.map((f) => safeString(f).trim()).filter(Boolean);
  }

  if (typeof direct === "string") {
    return direct
      .split(",")
      .map((f) => safeString(f).trim())
      .filter(Boolean);
  }

  const fromUtility = getAttentionFlags(ro);

  if (Array.isArray(fromUtility)) {
    return fromUtility.map((f) => safeString(f).trim()).filter(Boolean);
  }

  if (fromUtility && typeof fromUtility === "object") {
    const flags = [];

    Object.entries(fromUtility).forEach(([key, value]) => {
      if (!value) return;

      if (key === "waitingPartsTooLong") flags.push("Parts");
      else if (key === "promiseOverdue") flags.push("Promise");
      else if (key === "tagMissing") flags.push("Missing Tag");
      else flags.push(key);
    });

    return Array.from(
      new Set(flags.map((f) => safeString(f).trim()).filter(Boolean))
    );
  }

  return [];
}

function buildOperationalPatch(ro, options = {}) {
  const forceInProgress = !!options.forceInProgress;
  const activeWork =
    forceInProgress ||
    !!safeString(ro?.activeWorkStartedAtISO).trim() ||
    isInProgressRO(ro);

  if (activeWork) {
    return {
      status: roStatusValue("IN_PROGRESS", "IN_PROGRESS"),
      lifecycle: roLifecycleValue("IN_PROGRESS", "IN_PROGRESS"),
    };
  }

  if (ro?.assignedTechName || ro?.assignedTechId) {
    return {
      status: roStatusValue("ASSIGNED", "ASSIGNED"),
      lifecycle: roLifecycleValue("OPEN", "OPEN"),
    };
  }

  return {
    status: roStatusValue("AVAILABLE", "AVAILABLE"),
    lifecycle: roLifecycleValue("WAITING_FOR_ASSIGNMENT", "WAITING_FOR_ASSIGNMENT"),
  };
}
function buildActorMeta(currentUser, extra = {}) {
  const actorName = safeString(currentUser?.name).trim();
  const actorRole = safeString(currentUser?.role).trim();

  return {
    ...(extra && typeof extra === "object" ? extra : {}),
    actorUserId: safeString(currentUser?.id).trim(),
    actorName,
    actorRole,
    byName: safeString(extra?.byName).trim() || actorName,
    byRole: safeString(extra?.byRole).trim() || actorRole,
  };
}

function normalizeROSafe(input) {
  const repairedInput = repairVinAliases(input || {});
  const normalized = normalizeRO(repairedInput);

  const vin = normalizeVinLast8(
    firstNonBlank(
      normalized?.vinLast8,
      normalized?.vin_last_8,
      normalized?.vin_last8,
      normalized?.vinLast,
      normalized?.vin,
      repairedInput?.vinLast8,
      repairedInput?.vin
    )
  );

  const customerPickupStatus = firstNonBlank(
    normalized?.customerPickupStatus,
    normalized?.customer_pickup_status,
    repairedInput?.customerPickupStatus,
    repairedInput?.customer_pickup_status
  );

  const advisorActionStatus = firstNonBlank(
    normalized?.advisorActionStatus,
    normalized?.advisor_action_status,
    repairedInput?.advisorActionStatus,
    repairedInput?.advisor_action_status
  );

  const pickedUpAtISO = firstNonBlank(
    normalized?.pickedUpAtISO,
    normalized?.picked_up_at_iso,
    repairedInput?.pickedUpAtISO,
    repairedInput?.picked_up_at_iso
  );

  const vehicleGoneAtISO = firstNonBlank(
    normalized?.vehicleGoneAtISO,
    normalized?.vehicle_gone_at_iso,
    repairedInput?.vehicleGoneAtISO,
    repairedInput?.vehicle_gone_at_iso
  );

  return {
    ...normalized,
    vin,
    vinLast8: vin,
    vin_last_8: vin,
    vin_last8: vin,
    vinLast: vin,

    customerPickupStatus,
    customer_pickup_status: customerPickupStatus,

    advisorActionStatus,
    advisor_action_status: advisorActionStatus,

    customerContacted:
      normalized?.customerContacted ??
      normalized?.customer_contacted ??
      repairedInput?.customerContacted ??
      repairedInput?.customer_contacted ??
      false,
    customer_contacted:
      normalized?.customerContacted ??
      normalized?.customer_contacted ??
      repairedInput?.customerContacted ??
      repairedInput?.customer_contacted ??
      false,

    pickedUp:
      normalized?.pickedUp ??
      normalized?.picked_up ??
      repairedInput?.pickedUp ??
      repairedInput?.picked_up ??
      false,
    picked_up:
      normalized?.pickedUp ??
      normalized?.picked_up ??
      repairedInput?.pickedUp ??
      repairedInput?.picked_up ??
      false,

    vehicleGone:
      normalized?.vehicleGone ??
      normalized?.vehicle_gone ??
      repairedInput?.vehicleGone ??
      repairedInput?.vehicle_gone ??
      false,
    vehicle_gone:
      normalized?.vehicleGone ??
      normalized?.vehicle_gone ??
      repairedInput?.vehicleGone ??
      repairedInput?.vehicle_gone ??
      false,

    pickedUpAtISO,
    picked_up_at_iso: pickedUpAtISO,
    vehicleGoneAtISO,
    vehicle_gone_at_iso: vehicleGoneAtISO,
  };
}

/* -----------------------------------------
   Setup helpers
----------------------------------------- */
function normalizeSettings(raw) {
  const s = raw && typeof raw === "object" ? raw : {};
  const staffDirectory = Array.isArray(s.staffDirectory) ? s.staffDirectory : [];

  return {
    ...DEFAULT_SETTINGS,
    dealerName: safeString(s.dealerName || "").trim(),
    storeId: safeString(s.storeId || "default").trim() || "default",
    timezone:
      safeString(s.timezone || "America/Los_Angeles").trim() ||
      "America/Los_Angeles",
    setupCompleted: !!s.setupCompleted,
    staffDirectory: staffDirectory
      .map(normalizeStaffDirectoryPerson)
      .filter((p) => p.id && p.name && p.role),
  };
}

function normalizeDirectoryRole(role) {
  const r = safeString(role).toLowerCase().trim();

  if (!r) return "";
  if (r === "tech") return "technician";
  if (r === "service advisor") return "advisor";
  if (r === "service_advisor") return "advisor";
  if (r === "advisor") return "advisor";
  if (r === "service manager") return "manager";
  if (r === "service_manager") return "manager";
  if (r === "manager") return "manager";
  if (r === "shop foreman") return "foreman";
  if (r === "shop_foreman") return "foreman";
  if (r === "foreman") return "foreman";
  if (r === "parts manager") return "parts";
  if (r === "parts_manager") return "parts";
  if (r === "parts advisor") return "parts";
  if (r === "parts_advisor") return "parts";
  if (r === "parts") return "parts";
  if (r === "store admin") return "admin";
  if (r === "store_admin") return "admin";
  if (r === "admin") return "admin";
  if (r === "dispatcher") return "dispatcher";
  if (r === "dispatch") return "dispatcher";

  return r;
}

function normalizeStaffDirectoryPerson(p) {
  const employmentStatus = normalizeEmploymentStatus(
    p?.employmentStatus ??
      p?.employment_status ??
      p?.status ??
      p?.staffStatus ??
      p?.staff_status,
    p?.active ?? p?.isActive ?? p?.is_active
  );

  const activeValue = p?.active ?? p?.isActive ?? p?.is_active;

  return {
    id: safeString(
      p?.id || p?.staffId || p?.staff_id || p?.appUserId || p?.app_user_id
    ).trim(),
    appUserId: safeString(p?.appUserId || p?.app_user_id).trim(),
    businessUnitId: safeString(p?.businessUnitId || p?.business_unit_id).trim(),
    name: safeString(p?.name || p?.appUserName || p?.app_user_name).trim(),
    role: normalizeDirectoryRole(p?.role),
    email: safeString(p?.email || p?.appUserEmail || p?.app_user_email || "").trim(),
    employmentStatus,
    active:
      activeValue === undefined
        ? employmentStatus === EMPLOYMENT_STATUS.ACTIVE
        : !!activeValue,
  };
}

function normalizeDirectoryForTechs(dir) {
  const list = Array.isArray(dir) ? dir : [];
  return list
    .filter((p) => {
      const role = normalizeDirectoryRole(p?.role);
      const employmentStatus = normalizeEmploymentStatus(
        p?.employmentStatus ?? p?.employment_status,
        p?.active ?? p?.isActive ?? p?.is_active
      );
      const isActive = p?.active ?? p?.isActive ?? p?.is_active ?? false;

      return (
        role === "technician" &&
        !!isActive &&
        employmentStatus !== EMPLOYMENT_STATUS.TERMINATED
      );
    })
    .map((p) => ({
      id: safeString(
        p?.id || p?.staffId || p?.staff_id || p?.appUserId || p?.app_user_id
      ).trim(),
      name: safeString(p?.name || p?.appUserName || p?.app_user_name).trim(),
    }))
    .filter((t) => t.id && t.name);
}

function pickDefaultUserFromDirectory(dir) {
  const list = Array.isArray(dir) ? dir : [];

  const normalized = list
    .map((p) => {
      const role = normalizeDirectoryRole(p?.role);
      const employmentStatus = normalizeEmploymentStatus(
        p?.employmentStatus ?? p?.employment_status,
        p?.active
      );
      const isActive =
        p?.active === undefined
          ? employmentStatus === EMPLOYMENT_STATUS.ACTIVE
          : !!p.active;

      return {
        id: safeString(p?.id).trim(),
        name: safeString(p?.name).trim(),
        role,
        isActive,
      };
    })
    .filter((p) => p.id && p.name && p.role && p.isActive);

  const priority = [
    "admin",
    "manager",
    "foreman",
    "dispatcher",
    "advisor",
    "parts",
    "technician",
  ];

  for (const role of priority) {
    const found = normalized.find((p) => p.role === role);
    if (found) {
      return { id: found.id, name: found.name, role: found.role };
    }
  }
    return null;
}

function normalizeUser(u) {
  const obj = u && typeof u === "object" ? u : {};
  const role = safeString(obj.role).toLowerCase().trim();
  const id = safeString(obj.id).trim();
  const name = safeString(obj.name).trim();
  if (!id || !name) return null;
  return { id, name, role: role || "manager" };
}

/* -----------------------------------------
   Migration / normalization
----------------------------------------- */
function migrateStore(raw) {
  if (!raw) {
    return {
      version: SCHEMA_VERSION,
      ros: [],
      snapshots: DEFAULT_SNAPSHOTS,
      settings: normalizeSettings(DEFAULT_SETTINGS),
      currentUser: normalizeUser(DEFAULT_CURRENT_USER),
    };
  }

  if (
    typeof raw === "object" &&
    (Array.isArray(raw.ros) || Array.isArray(raw.repairOrders))
  ) {
    const rosArr = Array.isArray(raw.ros)
      ? raw.ros
      : Array.isArray(raw.repairOrders)
      ? raw.repairOrders
      : [];

    const settings = normalizeSettings(raw.settings || DEFAULT_SETTINGS);
    const cu =
      normalizeUser(raw.currentUser) ||
      pickDefaultUserFromDirectory(settings.staffDirectory) ||
      normalizeUser(DEFAULT_CURRENT_USER);

    return {
      version: SCHEMA_VERSION,
      ros: rosArr.map(normalizeROSafe),
      snapshots:
        typeof raw.snapshots === "object" && raw.snapshots
          ? raw.snapshots
          : DEFAULT_SNAPSHOTS,
      settings,
      currentUser: cu,
    };
  }

  if (Array.isArray(raw)) {
    return {
      version: SCHEMA_VERSION,
      ros: raw.map(normalizeROSafe),
      snapshots: DEFAULT_SNAPSHOTS,
      settings: normalizeSettings(DEFAULT_SETTINGS),
      currentUser: normalizeUser(DEFAULT_CURRENT_USER),
    };
  }

  return {
    version: SCHEMA_VERSION,
    ros: [],
    snapshots: DEFAULT_SNAPSHOTS,
    settings: normalizeSettings(DEFAULT_SETTINGS),
    currentUser: normalizeUser(DEFAULT_CURRENT_USER),
  };
}

/* -----------------------------------------
   Reducer
----------------------------------------- */
const ROContext = createContext(null);

const ACTIONS = Object.freeze({
  SET_ALL: "SET_ALL",
  ADD_RO: "ADD_RO",
  UPDATE_RO: "UPDATE_RO",
  DELETE_RO: "DELETE_RO",
  ARCHIVE_RO: "ARCHIVE_RO",
  UPSERT_WIP_SNAPSHOT: "UPSERT_WIP_SNAPSHOT",
  UPDATE_SETTINGS: "UPDATE_SETTINGS",
  SET_CURRENT_USER: "SET_CURRENT_USER",
});

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_ALL:
      return migrateStore(action.payload);

    case ACTIONS.UPDATE_SETTINGS: {
      const patch =
        action.payload && typeof action.payload === "object" ? action.payload : {};
      const next = normalizeSettings({
        ...(state.settings || DEFAULT_SETTINGS),
        ...patch,
      });
      return { ...state, settings: next };
    }

    case ACTIONS.SET_CURRENT_USER: {
      const next =
        normalizeUser(action.payload) ||
        state.currentUser ||
        normalizeUser(DEFAULT_CURRENT_USER);
      return { ...state, currentUser: next };
    }

    case ACTIONS.UPSERT_WIP_SNAPSHOT: {
      const snap = action.payload;
      const next = { ...(state.snapshots || {}) };
      next[snap.dateKey] = snap;
      return { ...state, snapshots: next };
    }

    case ACTIONS.ADD_RO: {
      const ro = normalizeROSafe(action.payload);
      const withEvents = addEvent(ro, "RO_CREATED", {
        roNumber: ro.roNumber,
        tagNumber: ro.tagNumber || "",
        advisorName: ro.advisorName || "",
        transportType: ro.transportType,
        workMix: ro.workMix,
        lifecycle: ro.lifecycle,
        status: ro.status,
      });
      return { ...state, ros: [withEvents, ...(state.ros || [])] };
    }

    case ACTIONS.UPDATE_RO: {
      const { id, patch, eventType, eventMeta } = action.payload;
      const normalizedPatch = canonicalizePatch(patch);

      return {
        ...state,
        ros: (state.ros || []).map((r) => {
          if (r.id !== id) return r;

          const merged = {
            ...r,
            ...normalizedPatch,
            timestamps: {
              ...(r.timestamps || {}),
              ...(normalizedPatch.timestamps || {}),
            },
          };

          const updated = normalizeROSafe(merged);
          return eventType ? addEvent(updated, eventType, eventMeta || {}) : updated;
        }),
      };
    }

    case ACTIONS.DELETE_RO:
      return {
        ...state,
        ros: (state.ros || []).filter((r) => r.id !== action.payload.id),
      };

    case ACTIONS.ARCHIVE_RO: {
      const { id } = action.payload;
      return {
        ...state,
        ros: (state.ros || []).map((r) => {
          if (r.id !== id) return r;
          const updated = normalizeROSafe({
            ...r,
            status: RO_STATUS.ARCHIVED,
            timestamps: { ...(r.timestamps || {}), archivedAt: nowISO() },
          });
          return addEvent(updated, "RO_ARCHIVED", {});
        }),
      };
    }

    default:
      return state;
  }
}

/* -----------------------------------------
   Provider + hook
----------------------------------------- */
function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return migrateStore(null);
    return migrateStore(JSON.parse(raw));
  } catch (e) {
    console.warn("Failed to load store cache:", e);
    return migrateStore(null);
  }
}

export function ROProvider({ children }) {
  const auth = useAuth();

  const token = safeString(auth?.token || "").trim();
  const authUser = auth?.user || null;
  const authMode = safeString(auth?.mode || "store").trim().toLowerCase();
  const effectiveStoreId = safeString(
    auth?.effectiveStoreId || authUser?.storeId || ""
  ).trim();

  const [state, dispatch] = useReducer(reducer, undefined, loadInitial);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSyncError, setLastSyncError] = useState("");
  const [lastSavedAtISO, setLastSavedAtISO] = useState("");

  const roSyncInFlightRef = useRef(false);

  const apiClient = useMemo(() => {
    const authHeaders = () => {
      const h = {};
      if (token) h.Authorization = `Bearer ${token}`;
      if (effectiveStoreId) h["X-Store-Id"] = effectiveStoreId;
      return h;
    };

    const withBust = (path) => {
      const sep = String(path).includes("?") ? "&" : "?";
      return `${path}${sep}_ts=${Date.now()}`;
    };

    const readJsonSafe = async (res) => {
      const contentType = safeString(res.headers?.get?.("content-type") || "");
      if (contentType.includes("application/json")) {
        return await res.json().catch(() => ({}));
      }
      const text = await res.text().catch(() => "");
      return text ? { message: text } : {};
    };

    const apiGet = async (path) => {
      const res = await fetch(apiUrl(withBust(path)), {
        method: "GET",
        headers: { ...authHeaders() },
        cache: "no-store",
      });
      const data = await readJsonSafe(res);
      if (!res.ok) {
        throw new Error(`GET ${path} failed: ${res.status} ${JSON.stringify(data)}`);
      }
      return data;
    };

    const apiPost = async (path, body) => {
      const res = await fetch(apiUrl(path), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(body || {}),
        cache: "no-store",
              });
      const data = await readJsonSafe(res);
      if (!res.ok) {
        const err = new Error(
          `POST ${path} failed: ${res.status} ${JSON.stringify(data)}`
        );
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    };

    const apiPatch = async (path, body) => {
      const res = await fetch(apiUrl(path), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(body || {}),
        cache: "no-store",
      });
      const data = await readJsonSafe(res);
      if (!res.ok) {
        throw new Error(`PATCH ${path} failed: ${res.status} ${JSON.stringify(data)}`);
      }
      return data;
    };

    const apiDelete = async (path) => {
      const res = await fetch(apiUrl(path), {
        method: "DELETE",
        headers: { ...authHeaders() },
        cache: "no-store",
      });

      const data = await readJsonSafe(res);

      if (!res.ok) {
        throw new Error(
          `DELETE ${path} failed: ${res.status} ${JSON.stringify(data)}`
        );
      }

      return data;
    };

    const apiUpload = async (path, formData) => {
      const res = await fetch(apiUrl(path), {
        method: "POST",
        headers: {
          ...authHeaders(),
        },
        body: formData,
        cache: "no-store",
      });

      const data = await readJsonSafe(res);

      if (!res.ok) {
        const err = new Error(
          `UPLOAD ${path} failed: ${res.status} ${JSON.stringify(data)}`
        );
        err.status = res.status;
        err.data = data;
        throw err;
      }

      return data;
    };

    return { apiGet, apiPost, apiPatch, apiDelete, apiUpload };
  }, [token, effectiveStoreId]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setIsLoading(true);
        setIsSyncing(true);
        setLastSyncError("");

        if (!authUser || !token) {
          const cached = loadInitial();
          if (!alive) return;
          dispatch({ type: ACTIONS.SET_ALL, payload: cached });
          setIsLoading(false);
          return;
        }

        if (authMode === "platform") {
          const cached = loadInitial();

          const safeSettings = normalizeSettings({
            ...(cached?.settings || DEFAULT_SETTINGS),
            storeId: effectiveStoreId || "",
          });

          const payload = {
            version: SCHEMA_VERSION,
            ros: Array.isArray(cached?.ros) ? cached.ros : [],
            snapshots:
              cached?.snapshots && typeof cached.snapshots === "object"
                ? cached.snapshots
                : DEFAULT_SNAPSHOTS,
            settings: safeSettings,
            currentUser:
              normalizeUser(cached?.currentUser) ||
              pickDefaultUserFromDirectory(safeSettings.staffDirectory) ||
              normalizeUser(DEFAULT_CURRENT_USER),
          };

          if (!alive) return;
          dispatch({ type: ACTIONS.SET_ALL, payload });
          setIsLoading(false);
          return;
        }

        const storeData = await apiClient.apiGet("/api/store");
        const store = storeData?.store || {};

        const roData = await apiClient.apiGet("/api/ros");
        const ros = Array.isArray(roData?.ros) ? roData.ros.map(normalizeROSafe) : [];

        const staffData = await apiClient.apiGet("/api/store/staff");
        const staff = Array.isArray(staffData?.staff) ? staffData.staff : [];

        const cached = loadInitial();

        let snapshots = DEFAULT_SNAPSHOTS;

        try {
          const snapshotData = await apiClient.apiGet("/api/internal/snapshots?limit=365");
          const rows = Array.isArray(snapshotData?.snapshots)
            ? snapshotData.snapshots
            : [];

          const nextSnapshots = {};

          for (const row of rows) {
            const key = safeString(row?.dateKey).trim();
            if (!key) continue;

            nextSnapshots[key] = {
              dateKey: key,
              capturedAtISO: row?.capturedAtISO || row?.captured_at || "",
              timezone:
                safeString(row?.timezone).trim() || "America/Los_Angeles",
              wipCount: safeNumber(row?.wipCount),
              waiterCount: safeNumber(row?.waiterCount),
              waitingOnPartsCount: safeNumber(row?.waitingOnPartsCount),
              dispatchCount: safeNumber(row?.dispatchCount),
              assignedCount: safeNumber(row?.assignedCount),
              inProgressCount: safeNumber(row?.inProgressCount),
              notes: safeString(row?.notes),
            };
          }

          snapshots = nextSnapshots;
        } catch (e) {
          console.warn("Initial snapshot load failed; using cached snapshots.", e);
          snapshots =
            cached?.snapshots && typeof cached.snapshots === "object"
              ? cached.snapshots
              : DEFAULT_SNAPSHOTS;
        }

        const storeId = effectiveStoreId || "default";
        const cachedSettings = normalizeSettings(cached?.settings || DEFAULT_SETTINGS);

        const settings = normalizeSettings({
          ...cachedSettings,
          storeId,
          dealerName: safeString(store?.dealerName || cachedSettings.dealerName || ""),
          timezone: safeString(
            store?.timezone || cachedSettings.timezone || "America/Los_Angeles"
          ),
          setupCompleted: !!store?.setupCompleted,
          staffDirectory: staff
            .map(normalizeStaffDirectoryPerson)
            .filter((p) => p.id && p.name && p.role),
        });

        const currentUser =
          normalizeUser({
            id:
              authUser?.id ||
              authUser?.userId ||
              authUser?.appUserId ||
              cached?.currentUser?.id,
            name: authUser?.name || cached?.currentUser?.name,
            role:
              normalizeDirectoryRole(authUser?.role || cached?.currentUser?.role) ||
              "manager",
          }) ||
          pickDefaultUserFromDirectory(settings.staffDirectory) ||
          normalizeUser(DEFAULT_CURRENT_USER);

        if (!alive) return;

        dispatch({
          type: ACTIONS.SET_ALL,
          payload: { version: SCHEMA_VERSION, ros, snapshots, settings, currentUser },
        });
      } catch (e) {
        console.warn("API sync failed; using local cache.", e);
        if (!alive) return;
        setLastSyncError(friendlyApiError(e, String(e?.message || e)));
        dispatch({ type: ACTIONS.SET_ALL, payload: loadInitial() });
      } finally {
        if (alive) {
          setIsLoading(false);
          setIsSyncing(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [authUser, token, apiClient, authMode, effectiveStoreId]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ ...state, version: SCHEMA_VERSION })
      );
    } catch (e) {
      console.warn("Failed to persist cache:", e);
    }
  }, [state]);

  useEffect(() => {
    function onStorage(e) {
      if (e.key !== STORAGE_KEY) return;
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        dispatch({ type: ACTIONS.SET_ALL, payload: JSON.parse(raw) });
      } catch {
        // ignore
      }
    }

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const api = useMemo(() => {
    const isImpersonating = !!auth?.isImpersonating;

    const assertCanMutate = () => {
      if (isImpersonating) {
        throw new Error("Editing is disabled while impersonating.");
      }
    };

    const ros = Array.isArray(state.ros) ? state.ros.map(normalizeROSafe) : [];
    const snapshots =
      typeof state.snapshots === "object" && state.snapshots
        ? state.snapshots
        : DEFAULT_SNAPSHOTS;

    const settings = normalizeSettings(state.settings || DEFAULT_SETTINGS);
    const techs = normalizeDirectoryForTechs(settings.staffDirectory);

    const currentUser =
      normalizeUser({
        id:
          authUser?.id ||
          authUser?.userId ||
          authUser?.appUserId ||
          state.currentUser?.id,
        name: authUser?.name || state.currentUser?.name,
        role: normalizeDirectoryRole(authUser?.role || state.currentUser?.role),
      }) ||
      pickDefaultUserFromDirectory(settings.staffDirectory) ||
      normalizeUser(DEFAULT_CURRENT_USER);

    const activeStoreId =
      safeString(effectiveStoreId).trim() ||
      safeString(settings.storeId).trim() ||
      "default";

    const storeTimeZone =
      safeString(settings.timezone).trim() || "America/Los_Angeles";

    const staffDirectory = Array.isArray(settings.staffDirectory)
      ? settings.staffDirectory
      : [];

    const refreshStaff = async () => {
      if (!token || !authUser) return staffDirectory;

      const staffData = await apiClient.apiGet("/api/store/staff");
      const staff = Array.isArray(staffData?.staff) ? staffData.staff : [];

      const normalized = staff
        .map(normalizeStaffDirectoryPerson)
        .filter((p) => p.id && p.name && p.role);

      dispatch({ type: ACTIONS.UPDATE_SETTINGS, payload: { staffDirectory: normalized } });
      return normalized;
    };

    const refreshSnapshots = async () => {
      if (!token || !authUser || authMode === "platform") {
        return DEFAULT_SNAPSHOTS;
      }

      try {
        const result = await apiClient.apiGet("/api/internal/snapshots?limit=365");
        const rows = Array.isArray(result?.snapshots) ? result.snapshots : [];
        const next = {};

        for (const row of rows) {
          const key = safeString(row?.dateKey).trim();
          if (!key) continue;

          next[key] = {
            dateKey: key,
            capturedAtISO: row?.capturedAtISO || row?.captured_at || "",
            timezone: safeString(row?.timezone).trim() || "America/Los_Angeles",
            wipCount: safeNumber(row?.wipCount),
            waiterCount: safeNumber(row?.waiterCount),
            waitingOnPartsCount: safeNumber(row?.waitingOnPartsCount),
            dispatchCount: safeNumber(row?.dispatchCount),
            assignedCount: safeNumber(row?.assignedCount),
            inProgressCount: safeNumber(row?.inProgressCount),
            notes: safeString(row?.notes),
          };
        }

        return next;
      } catch (e) {
        console.error("Snapshot refresh failed:", e);
        return DEFAULT_SNAPSHOTS;
      }
    };
        const refreshStoreData = async () => {
      if (!token || !authUser || authMode === "platform") return false;

      try {
        setIsSyncing(true);
        setLastSyncError("");

        const storeData = await apiClient.apiGet("/api/store");
        const store = storeData?.store || {};

        const roData = await apiClient.apiGet("/api/ros");
        const freshROs = Array.isArray(roData?.ros)
          ? roData.ros.map(normalizeROSafe)
          : [];

        const staffData = await apiClient.apiGet("/api/store/staff");
        const staff = Array.isArray(staffData?.staff) ? staffData.staff : [];

        const nextSettings = normalizeSettings({
          ...(state.settings || DEFAULT_SETTINGS),
          storeId: activeStoreId,
          dealerName: safeString(store?.dealerName || state.settings?.dealerName || ""),
          timezone: safeString(
            store?.timezone || state.settings?.timezone || "America/Los_Angeles"
          ),
          setupCompleted:
            store?.setupCompleted ?? state.settings?.setupCompleted ?? false,
          staffDirectory: staff
            .map(normalizeStaffDirectoryPerson)
            .filter((p) => p.id && p.name && p.role),
        });

        dispatch({
          type: ACTIONS.SET_ALL,
          payload: {
            version: SCHEMA_VERSION,
            ros: freshROs,
            snapshots: await refreshSnapshots(),
            settings: nextSettings,
            currentUser:
              normalizeUser({
                id:
                  authUser?.id ||
                  authUser?.userId ||
                  authUser?.appUserId ||
                  state.currentUser?.id,
                name: authUser?.name || state.currentUser?.name,
                role:
                  normalizeDirectoryRole(authUser?.role || state.currentUser?.role) ||
                  "manager",
              }) ||
              pickDefaultUserFromDirectory(nextSettings.staffDirectory) ||
              normalizeUser(DEFAULT_CURRENT_USER),
          },
        });

        return true;
      } catch (e) {
        console.error(e);
        setLastSyncError(friendlyApiError(e, String(e?.message || e)));
        return false;
      } finally {
        setIsSyncing(false);
      }
    };

    const refreshROsOnly = async () => {
      if (!token || !authUser || authMode === "platform") return false;
      if (roSyncInFlightRef.current) return false;

      roSyncInFlightRef.current = true;

      try {
        setIsSyncing(true);
        setLastSyncError("");

        const roData = await apiClient.apiGet("/api/ros");
        const freshROs = Array.isArray(roData?.ros)
          ? roData.ros.map(normalizeROSafe)
          : [];

        dispatch({
          type: ACTIONS.SET_ALL,
          payload: {
            version: SCHEMA_VERSION,
            ros: freshROs,
            snapshots: state.snapshots || DEFAULT_SNAPSHOTS,
            settings: state.settings || normalizeSettings(DEFAULT_SETTINGS),
            currentUser:
              state.currentUser ||
              normalizeUser(DEFAULT_CURRENT_USER),
          },
        });

        return true;
      } catch (e) {
        console.error(e);
        setLastSyncError(friendlyApiError(e, String(e?.message || e)));
        return false;
      } finally {
        roSyncInFlightRef.current = false;
        setIsSyncing(false);
      }
    };

    const createStaff = async ({ name, role, email }) => {
      assertCanMutate();
      if (!token || !authUser) throw new Error("Not authenticated.");

      setIsSaving(true);
      try {
        await apiClient.apiPost("/api/staff", {
          name: safeString(name).trim(),
          role: safeString(role).toLowerCase().trim(),
          email: safeString(email || "").trim().toLowerCase(),
          employmentStatus: EMPLOYMENT_STATUS.ACTIVE,
        });

        await refreshStaff();
        setLastSavedAtISO(nowISO());
        return true;
      } finally {
        setIsSaving(false);
      }
    };

    const updateStaff = async (id, patch = {}) => {
      assertCanMutate();

      const staffId = safeString(id).trim();
      if (!staffId) throw new Error("Missing staff id.");
      if (!token || !authUser) throw new Error("Not authenticated.");

      setIsSaving(true);
      try {
        await apiClient.apiPatch(`/api/staff/${encodeURIComponent(staffId)}`, {
          ...patch,
        });

        await refreshStaff();
        setLastSavedAtISO(nowISO());
        return true;
      } finally {
        setIsSaving(false);
      }
    };

    const updateSettings = async (patch = {}) => {
      assertCanMutate();

      dispatch({ type: ACTIONS.UPDATE_SETTINGS, payload: patch });
      setLastSavedAtISO(nowISO());

      return true;
    };

    const setCurrentUser = (user) => {
      dispatch({ type: ACTIONS.SET_CURRENT_USER, payload: user });
    };

    const validateTagNumber = (tagNumber, ignoreId = "") => {
      return validateTagNumberUnique(ros, tagNumber, ignoreId);
    };

    const persistPatch = async (
      id,
      patch = {},
      eventType = "RO_UPDATED",
      eventMeta = {}
    ) => {
      assertCanMutate();

      const roId = safeString(id).trim();
      if (!roId) throw new Error("Missing RO id.");

      const current = ros.find((r) => r.id === roId);
      if (!current) throw new Error("RO not found.");

      const safePatch = repairVinAliases({
        ...(patch || {}),
      });

      const normalizedPatch = canonicalizePatch(safePatch);
      const requestPatch = stripReadOnlyRoPatchFields(normalizedPatch);

      const actorMeta = buildActorMeta(currentUser, {
        ...(eventMeta || {}),
      });

      dispatch({
        type: ACTIONS.UPDATE_RO,
        payload: {
          id: roId,
          patch: normalizedPatch,
          eventType,
          eventMeta: actorMeta,
        },
      });

      if (!token || !authUser || authMode === "platform") {
        setLastSavedAtISO(nowISO());
        return normalizeROSafe({ ...current, ...normalizedPatch });
      }

      setIsSaving(true);

      try {
        /*
          Important:
          Backend expects the changed RO fields at the top level.
          Do not nest them under { patch: ... }, or the save can appear
          successful locally but revert after refresh.
        */
        const saved = await apiClient.apiPatch(
          `/api/ros/${encodeURIComponent(roId)}`,
          {
            ...requestPatch,
            eventType,
            eventMeta: actorMeta,
          }
        );

        setLastSavedAtISO(nowISO());

        if (saved?.ro) {
          const savedRO = normalizeROSafe(saved.ro);

          dispatch({
            type: ACTIONS.UPDATE_RO,
            payload: {
              id: roId,
              patch: savedRO,
              eventType: "",
              eventMeta: {},
            },
          });

          window.setTimeout(() => {
            refreshROsOnly().catch(() => {});
          }, 250);

          return savedRO;
        }

        const fallbackRO = normalizeROSafe({ ...current, ...normalizedPatch });

        window.setTimeout(() => {
          refreshROsOnly().catch(() => {});
        }, 250);

        return fallbackRO;
      } catch (e) {
        setLastSyncError(friendlyApiError(e, String(e?.message || e)));
        await refreshStoreData();
        throw e;
      } finally {
        setIsSaving(false);
      }
    };

    const addRO = async (input = {}) => {
      assertCanMutate();

      const stamp = nowISO();

      const cleaned = repairVinAliases({
        ...(input || {}),
        id: safeString(input?.id).trim() || genId("ro"),
        status:
          safeString(input?.status).trim() ||
          roStatusValue("AVAILABLE", "AVAILABLE"),
        lifecycle:
          safeString(input?.lifecycle).trim() ||
          roLifecycleValue(
            "WAITING_FOR_ASSIGNMENT",
            "WAITING_FOR_ASSIGNMENT"
          ),
        partsStatus:
          safeString(input?.partsStatus).trim() ||
          partsStatusValue("NONE", "NONE"),
        partsActionStatus:
          safeString(input?.partsActionStatus).trim() ||
          partsActionValue("NONE", "NONE"),
        partsRequestType: safeString(input?.partsRequestType).trim() || "",

        customerPickupStatus: safeString(input?.customerPickupStatus).trim() || "",
        customer_pickup_status: safeString(input?.customerPickupStatus).trim() || "",
        advisorActionStatus: safeString(input?.advisorActionStatus).trim() || "",
        advisor_action_status: safeString(input?.advisorActionStatus).trim() || "",
        customerContacted: !!input?.customerContacted,
        customer_contacted: !!input?.customerContacted,
        pickedUp: !!input?.pickedUp,
        picked_up: !!input?.pickedUp,
        vehicleGone: !!input?.vehicleGone,
        vehicle_gone: !!input?.vehicleGone,

        createdAt: input?.createdAt || stamp,
        updatedAt: stamp,
        timestamps: {
          ...(input?.timestamps || {}),
          createdAt: input?.timestamps?.createdAt || input?.createdAt || stamp,
          updatedAt: stamp,
        },
      });

      const tagCheck = validateTagNumber(cleaned.tagNumber, cleaned.id);
      if (!tagCheck.ok) {
        throw new Error(tagCheck.message || "Tag # is already in use on an OPEN RO.");
      }

      const ro = normalizeROSafe(cleaned);

      const actorMeta = buildActorMeta(currentUser, {
        roNumber: ro.roNumber,
        tagNumber: ro.tagNumber || "",
        advisorName: ro.advisorName || "",
        transportType: ro.transportType,
        workMix: ro.workMix,
        lifecycle: ro.lifecycle,
        status: ro.status,
      });

      dispatch({ type: ACTIONS.ADD_RO, payload: ro });

      if (!token || !authUser || authMode === "platform") {
        setLastSavedAtISO(nowISO());
        return ro;
      }

      setIsSaving(true);

      try {
        const saved = await apiClient.apiPost("/api/ros", {
          ...ro,
          eventType: "RO_CREATED",
          eventMeta: actorMeta,
        });

        const savedRO = normalizeROSafe(saved?.ro || saved?.repairOrder || ro);

        dispatch({
          type: ACTIONS.UPDATE_RO,
          payload: {
            id: ro.id,
            patch: savedRO,
            eventType: "",
            eventMeta: {},
          },
        });

        setLastSavedAtISO(nowISO());
        return savedRO;
      } catch (e) {
        setLastSyncError(friendlyApiError(e, String(e?.message || e)));
        await refreshStoreData();
        throw e;
      } finally {
        setIsSaving(false);
      }
    };
        const updateRO = async (
      id,
      patch = {},
      eventType = "RO_EDITED",
      eventMeta = {}
    ) => {
      const nextPatch = canonicalizePatch({
        ...(patch || {}),
        timestamps: {
          ...(patch?.timestamps || {}),
          updatedAt: patch?.timestamps?.updatedAt || nowISO(),
        },
      });

      return persistPatch(id, nextPatch, eventType, eventMeta);
    };

    const deleteRO = async (id) => {
      assertCanMutate();

      const roId = safeString(id).trim();
      if (!roId) throw new Error("Missing RO id.");

      dispatch({ type: ACTIONS.DELETE_RO, payload: { id: roId } });

      if (!token || !authUser || authMode === "platform") {
        setLastSavedAtISO(nowISO());
        return true;
      }

      setIsSaving(true);

      try {
        await apiClient.apiDelete(`/api/ros/${encodeURIComponent(roId)}`);
        setLastSavedAtISO(nowISO());
        return true;
      } catch (e) {
        setLastSyncError(friendlyApiError(e, String(e?.message || e)));
        await refreshStoreData();
        throw e;
      } finally {
        setIsSaving(false);
      }
    };

    const archiveRO = async (id) => {
      const ro = ros.find((r) => r.id === id);
      const stamp = nowISO();

      return updateRO(
        id,
        {
          status: roStatusValue("ARCHIVED", "ARCHIVED"),
          lifecycle: roLifecycleValue("ARCHIVED", "ARCHIVED"),
          timestamps: {
            ...(ro?.timestamps || {}),
            archivedAt: stamp,
            updatedAt: stamp,
          },
        },
        "RO_ARCHIVED",
        { roNumber: ro?.roNumber }
      );
    };

    const assignTech = async (id, techIdOrName, maybeName = "") => {
      const ro = ros.find((r) => r.id === id);
      const techId = safeString(techIdOrName).trim();

      const foundTech =
        techs.find((t) => t.id === techId) ||
        techs.find((t) => t.name === techIdOrName) ||
        null;

      const techName = safeString(maybeName || foundTech?.name || techIdOrName).trim();
      const stamp = nowISO();

      return updateRO(
        id,
        {
          assignedTechId: foundTech?.id || techId,
          assignedTechName: techName,
          status: roStatusValue("ASSIGNED", "ASSIGNED"),
          lifecycle: roLifecycleValue("OPEN", "OPEN"),
          timestamps: {
            ...(ro?.timestamps || {}),
            assignedAt: stamp,
            updatedAt: stamp,
          },
        },
        "TECH_ASSIGNED",
        {
          roNumber: ro?.roNumber,
          techId: foundTech?.id || techId,
          techName,
        }
      );
    };

    const unassignTech = async (id) => {
      const ro = ros.find((r) => r.id === id);
      const stamp = nowISO();

      return updateRO(
        id,
        {
          assignedTechId: "",
          assignedTechName: "",
          status: roStatusValue("AVAILABLE", "AVAILABLE"),
          lifecycle: roLifecycleValue("WAITING_FOR_ASSIGNMENT", "WAITING_FOR_ASSIGNMENT"),
          timestamps: {
            ...(ro?.timestamps || {}),
            assignedAt: "",
            unassignedAt: stamp,
            updatedAt: stamp,
          },
        },
        "TECH_UNASSIGNED",
        { roNumber: ro?.roNumber }
      );
    };

    const startWork = async (id) => {
      const ro = ros.find((r) => r.id === id);
      const stamp = nowISO();

      // Start owns work state only; preserve Parts state and its attention flags.
      // Timestamp patches are merged by the reducer/server, so do not resend
      // assignment or Parts timestamps that require separate authorization.
      const patch = {
        activeWorkStartedAtISO: stamp,
        active_work_started_at_iso: stamp,
        lastStoppedAtISO: "",
        last_stopped_at_iso: "",
        workEndedAtISO: "",
        status: roStatusValue("IN_PROGRESS", "IN_PROGRESS"),
        lifecycle: roLifecycleValue("IN_PROGRESS", "IN_PROGRESS"),
        timestamps: {
          activeWorkStartedAtISO: stamp,
          activeWorkStartedAt: stamp,
          stoppedAt: "",
          workEndAt: "",
          lastStoppedAtISO: "",
          workStartAt: stamp,
          updatedAt: stamp,
        },
      };

      return updateRO(id, patch, "WORK_STARTED", { roNumber: ro?.roNumber });
    };

    const stopWork = async (id, options = {}) => {
      const ro = ros.find((r) => r.id === id);
      const stamp = nowISO();

      const startedAt = safeString(ro?.activeWorkStartedAtISO).trim();
      const durationSec = startedAt ? secBetween(startedAt, stamp) : 0;

      const existingSec = safeNumber(ro?.totalWorkSeconds);
      const totalWorkSeconds = existingSec + durationSec;

      const workSessions = Array.isArray(ro?.timestamps?.workSessions)
        ? ro.timestamps.workSessions
        : [];

      return updateRO(
        id,
        {
          activeWorkStartedAtISO: "",
          totalWorkSeconds,
          lastStoppedAtISO: stamp,
          status: roStatusValue("ASSIGNED", "ASSIGNED"),
          lifecycle: roLifecycleValue("OPEN", "OPEN"),
          flaggedHours: safeNumber(options?.flaggedHours ?? ro?.flaggedHours),
          completedHours: safeNumber(options?.completedHours ?? ro?.completedHours),
          timestamps: {
            activeWorkStartedAtISO: "",
            stoppedAt: stamp,
            workEndAt: stamp,
            workSessions: startedAt
              ? [
                  ...workSessions,
                  {
                    startedAtISO: startedAt,
                    stoppedAtISO: stamp,
                    seconds: durationSec,
                  },
                ]
              : workSessions,
            updatedAt: stamp,
          },
        },
        "WORK_STOPPED",
        {
          roNumber: ro?.roNumber,
          durationSec,
          totalWorkSeconds,
          flaggedHours: safeNumber(options?.flaggedHours ?? ro?.flaggedHours),
          completedHours: safeNumber(options?.completedHours ?? ro?.completedHours),
        }
      );
    };

    const endWork = stopWork;

    const completeWork = async (id, options = {}) => {
      const ro = ros.find((r) => r.id === id);
      const stamp = nowISO();

      const startedAt = safeString(ro?.activeWorkStartedAtISO).trim();
      const durationSec = startedAt ? secBetween(startedAt, stamp) : 0;
      const totalWorkSeconds = safeNumber(ro?.totalWorkSeconds) + durationSec;

      const completedHours = safeNumber(
        options?.completedHours ?? options?.hoursCompleted ?? ro?.completedHours
      );

      const flaggedHours = safeNumber(
        options?.flaggedHours ?? options?.hoursFlagged ?? ro?.flaggedHours
      );

      return updateRO(
        id,
        {
          activeWorkStartedAtISO: "",
          totalWorkSeconds,
          completedHours,
          hoursCompleted: completedHours,
          flaggedHours,
          hoursFlagged: flaggedHours,
          status: roStatusValue("COMPLETED", "COMPLETED"),
          lifecycle: roLifecycleValue("COMPLETED", "COMPLETED"),
          technicianCompletedAtISO: stamp,
          completedAtISO: stamp,
          timestamps: {
            activeWorkStartedAtISO: "",
            completedAt: stamp,
            technicianCompletedAt: stamp,
            updatedAt: stamp,
          },
        },
        "WORK_COMPLETED",
        {
          roNumber: ro?.roNumber,
          totalWorkSeconds,
          completedHours,
          flaggedHours,
          techId: options?.techId || ro?.assignedTechId || "",
          techName: options?.techName || ro?.assignedTechName || "",
        }
      );
    };

    const completeRO = async (id, meta = {}) => {
      const ro = ros.find((r) => r.id === id);
      const stamp = nowISO();

      return updateRO(
        id,
        {
          status: roStatusValue("COMPLETED", "COMPLETED"),
          lifecycle: roLifecycleValue("COMPLETED", "COMPLETED"),
          completedAtISO: stamp,
          timestamps: {
            ...(ro?.timestamps || {}),
            completedAt: stamp,
            updatedAt: stamp,
          },
        },
        "RO_COMPLETED",
        {
          roNumber: ro?.roNumber,
          source: meta?.source || "manual",
        }
      );
    };
        const requestParts = async (
      id,
      requestType = PARTS_REQUEST_TYPE.NEED_PARTS,
      meta = {}
    ) => {
      const ro = ros.find((r) => r.id === id);
      if (!ro) throw new Error("RO not found.");

      const rt = actionKey(requestType) === "NEED_PRICE"
        ? PARTS_REQUEST_TYPE.NEED_PRICE
        : PARTS_REQUEST_TYPE.NEED_PARTS;

      const note = safeString(
        meta?.partsRequestNote ||
          meta?.note ||
          meta?.noteText ||
          meta?.partsNote ||
          ""
      ).trim();

      const existingFlags = getAttentionFlagList(ro);
      const nextFlags = Array.from(new Set([...existingFlags, "Parts"]));

      const nextPatch = {
        partsRequestType: rt,
        partsRequestNote: note,
        partsActionStatus: partsActionValue("REVIEW", "REVIEW"),
        partsStatus: partsStatusValue("NONE", "NONE"),
        status: roStatusValue("WAITING_ON_PARTS", "WAITING_ON_PARTS"),
        lifecycle: roLifecycleValue("WAITING_FOR_PARTS", "WAITING_FOR_PARTS"),
        attentionFlags: nextFlags,
        timestamps: {
          ...(ro?.timestamps || {}),
          partsRequestedAt: nowISO(),
          updatedAt: nowISO(),
        },
      };

      return updateRO(id, nextPatch, "PARTS_REQUESTED", {
        roNumber: ro?.roNumber,
        partsRequestType: rt,
        notePreview: note.slice(0, 160),
        byRole: meta?.byRole || meta?.actorRole || currentUser?.role || "",
        byName: meta?.byName || meta?.actorName || currentUser?.name || "",
      });
    };

    const requestPartsAction = async (
      id,
      noteOrRequestType = PARTS_REQUEST_TYPE.NEED_PARTS,
      maybeRequestType = "",
      byRole = "",
      byName = ""
    ) => {
      const first = actionKey(noteOrRequestType);
      const second = actionKey(maybeRequestType);

      const requestType =
        second === "NEED_PRICE" || second === "NEED_PARTS"
          ? maybeRequestType
          : first === "NEED_PRICE" || first === "NEED_PARTS"
          ? noteOrRequestType
          : PARTS_REQUEST_TYPE.NEED_PARTS;

      const note =
        second === "NEED_PRICE" || second === "NEED_PARTS"
          ? safeString(noteOrRequestType).trim()
          : "";

      return requestParts(id, requestType, {
        partsRequestNote: note,
        byRole,
        byName,
      });
    };

    const requestPartsReview = requestPartsAction;

    const updatePartsAction = async (id, action, meta = {}) => {
      const ro = ros.find((r) => r.id === id);
      if (!ro) throw new Error("RO not found.");

      const rawActionKey = actionKey(action);
      let normalizedAction = normalizePartsActionStatus(action);

      if (rawActionKey === "NO_ACTION") normalizedAction = "NO_ACTION";
      if (rawActionKey === "PRE_PULLED" || rawActionKey === "PREPULLED" || rawActionKey === "PULLED") {
        normalizedAction = partsActionValue("PRE_PULLED", "PRE_PULLED");
      }
      if (rawActionKey === "ORDERED") normalizedAction = "ORDERED";
      if (rawActionKey === "ARRIVED") normalizedAction = "ARRIVED";
      if (rawActionKey === "QUOTED" || rawActionKey === "QUOTE_PROVIDED") normalizedAction = "QUOTED";
      if (rawActionKey === "NONE" || rawActionKey === "CLEAR") {
        normalizedAction = partsActionValue("NONE", "NONE");
      }
      if (rawActionKey === "REVIEW" || rawActionKey === "REOPEN" || rawActionKey === "OPEN") {
        normalizedAction = partsActionValue("REVIEW", "REVIEW");
      }

      const normalizedActionKey = actionKey(normalizedAction);
      const isNoAction =
        normalizedActionKey === "NO_ACTION" ||
        normalizedActionKey === "NONE" ||
        normalizedActionKey === "CLEAR";

      const reviewAction = isPartsReviewAction(normalizedAction);

      const requestType = reviewAction
        ? safeString(
            meta?.partsRequestType ||
              ro?.partsRequestType ||
              PARTS_REQUEST_TYPE.NEED_PARTS
          ).trim()
        : "";

      const currentPartsStatus = normalizePartsStatus(ro?.partsStatus);

      let nextPartsStatus = isNoAction
        ? partsStatusValue("NONE", "NONE")
        : inferPartsStatusFromAction(normalizedAction, requestType, currentPartsStatus);

      if (normalizedActionKey === "ORDERED") nextPartsStatus = partsStatusValue("ORDERED", "ORDERED");
      if (normalizedActionKey === "ARRIVED") nextPartsStatus = partsStatusValue("ARRIVED", "ARRIVED");
      if (normalizedActionKey === "QUOTED") nextPartsStatus = partsStatusValue("QUOTED", "QUOTED");
      if (normalizedActionKey === "PRE_PULLED" || normalizedActionKey === "PREPULLED") {
        nextPartsStatus = partsStatusValue("PULLED", "PULLED");
      }

      const orderedHold = actionKey(nextPartsStatus) === "ORDERED";
      const activeReview = reviewAction && !!requestType;

      let nextStatePatch;
      if (activeReview || orderedHold) {
        nextStatePatch = {
          status: roStatusValue("WAITING_ON_PARTS", "WAITING_ON_PARTS"),
          lifecycle: roLifecycleValue("WAITING_FOR_PARTS", "WAITING_FOR_PARTS"),
        };
      } else {
        nextStatePatch = buildOperationalPatch(ro);
      }

      const existingFlags = getAttentionFlagList(ro);
      const keepPartsAttention = activeReview || orderedHold;
      const nextFlags = keepPartsAttention
        ? Array.from(new Set([...existingFlags, "Parts"]))
        : existingFlags.filter((f) => safeString(f).toLowerCase() !== "parts");

      const patch = {
        ...nextStatePatch,
        partsActionStatus: isNoAction
          ? partsActionValue("NO_ACTION", "NO_ACTION")
          : normalizedAction,
        partsStatus: nextPartsStatus,
        partsRequestType: activeReview ? requestType : "",
        partsRequestNote: activeReview ? safeString(ro?.partsRequestNote).trim() : "",
        attentionFlags: nextFlags,
        partsActionedAtISO: nowISO(),
        partsLastActionAtISO: nowISO(),
        timestamps: {
          ...(ro?.timestamps || {}),
          partsActionedAt: nowISO(),
          updatedAt: nowISO(),
        },
      };

      if (meta?.eta !== undefined) {
        patch.partsEta = safeString(meta.eta).trim();
        patch.partsEtaISO = safeString(meta.eta).trim();
      }

      if (meta?.partsNote !== undefined) {
        patch.partsNote = safeString(meta.partsNote).trim();
      }

      return updateRO(id, patch, "PARTS_ACTION_UPDATED", {
        roNumber: ro?.roNumber,
        partsActionStatus: patch.partsActionStatus,
        partsStatus: nextPartsStatus,
        partsRequestType: patch.partsRequestType,
        eta: meta?.eta,
        byRole: meta?.byRole || meta?.actorRole || currentUser?.role || "",
        byName: meta?.byName || meta?.actorName || currentUser?.name || "",
      });
    };

    const reopenPartsReview = async (
      id,
      requestType = PARTS_REQUEST_TYPE.NEED_PARTS
    ) => {
      return requestParts(id, requestType, {
        byRole: currentUser?.role || "",
        byName: currentUser?.name || "",
      });
    };

    const updatePartsStatus = async (id, partsStatus, meta = {}) => {
      const key = actionKey(partsStatus);
      const action =
        meta?.partsActionStatus ||
        (key === "PULLED"
          ? partsActionValue("PRE_PULLED", "PRE_PULLED")
          : partsStatus);

      return updatePartsAction(id, action, {
        ...meta,
        partsRequestType: meta?.partsRequestType || "",
      });
    };

    const setPartsActionStatus = async (id, action, meta = {}) => {
      const safeMeta =
        meta && typeof meta === "object"
          ? meta
          : { partsNote: safeString(meta).trim() };

      return updatePartsAction(id, action, safeMeta);
    };

    const setPartsStatus = async (id, partsStatus, meta = {}) => {
      const safeMeta =
        meta && typeof meta === "object"
          ? meta
          : { partsNote: safeString(meta).trim() };

      return updatePartsStatus(id, partsStatus, safeMeta);
    };

    const savePartsEta = async (id, eta) => {
      const ro = ros.find((r) => r.id === id);
      const cleanEta = safeString(eta).trim();

      return updateRO(
        id,
        {
          partsEta: cleanEta,
          timestamps: {
            ...(ro?.timestamps || {}),
            partsEtaUpdatedAt: nowISO(),
            updatedAt: nowISO(),
          },
        },
        "PARTS_ETA_UPDATED",
        {
          roNumber: ro?.roNumber,
          eta: cleanEta,
        }
      );
    };

    const setPartsEta = savePartsEta;
    const updatePartsEta = savePartsEta;

    const markCustomerContacted = async (id, meta = {}) => {
      const ro = ros.find((r) => r.id === id);
      const stamp = nowISO();

      return updateRO(
        id,
        {
          customerPickupStatus: "CONTACTED_FOR_PICKUP",
          customer_pickup_status: "CONTACTED_FOR_PICKUP",

          advisorActionStatus: "CUSTOMER_CONTACTED",
          advisor_action_status: "CUSTOMER_CONTACTED",

          customerContacted: true,
          customer_contacted: true,
          customerContactedAtISO: stamp,
          customerContactedAt: stamp,
          customer_contacted_at_iso: stamp,
          customer_contacted_at: stamp,
          customerContactedByName: currentUser?.name || "",
          customerContactedByRole: currentUser?.role || "",

          vehicleGone: false,
          vehicle_gone: false,
          pickedUp: false,
          picked_up: false,
          pickedUpToday: false,

          timestamps: {
            ...(ro?.timestamps || {}),
            customerContactedAt: stamp,
            updatedAt: stamp,
          },
        },
        "CUSTOMER_CONTACTED",
        {
          roNumber: ro?.roNumber,
          note: meta?.note,
          source: meta?.source || "advisor-log",
        }
      );
    };

    const setCustomerContactStatus = async (id, payload = {}) => {
      if (payload?.contacted === false) {
        return clearCustomerContactStatus(id);
      }

      return markCustomerContacted(id, payload);
    };

    const clearCustomerContactStatus = async (id) => {
      const ro = ros.find((r) => r.id === id);
      const stamp = nowISO();

      return updateRO(
        id,
        {
          customerContacted: false,
          customer_contacted: false,
          customerContactNote: "",
          customerContactedAt: "",
          customerContactedAtISO: "",
          customer_contacted_at: "",
          customer_contacted_at_iso: "",
          customerContactedByRole: "",
          customerContactedByName: "",

          advisorActionStatus: "",
          advisor_action_status: "",
          customerPickupStatus: "",
          customer_pickup_status: "",

          timestamps: {
            ...(ro?.timestamps || {}),
            updatedAt: stamp,
          },
        },
        "CUSTOMER_CONTACT_CLEARED",
        { roNumber: ro?.roNumber }
      );
    };
    const listAttachmentsForRO = async (id) => {
      const roId = safeString(id).trim();

      if (!roId) {
        throw new Error("Missing RO id.");
      }

      if (!token || !authUser || authMode === "platform") {
        return [];
      }

      const data = await apiClient.apiGet(
        `/api/attachments/ro/${encodeURIComponent(roId)}`
      );

      return Array.isArray(data?.attachments) ? data.attachments : [];
    };

    const getAttachmentSignedUrl = async (attachmentId) => {
      const cleanId = safeString(attachmentId).trim();

      if (!cleanId) {
        throw new Error("Missing attachment id.");
      }

      if (!token || !authUser || authMode === "platform") {
        throw new Error("Attachment viewing requires an active store session.");
      }

      const data = await apiClient.apiGet(
        `/api/attachments/${encodeURIComponent(cleanId)}/signed-url`
      );

      return data?.url || "";
    };

    const uploadAttachmentsForRO = async (id, files, options = {}) => {
      assertCanMutate();

      const roId = safeString(id).trim();

      if (!roId) {
        throw new Error("Missing RO id.");
      }

      const fileList = Array.from(files || []).filter(Boolean);

      if (!fileList.length) {
        throw new Error("Select at least one photo or video.");
      }

      if (!token || !authUser || authMode === "platform") {
        throw new Error("Attachment uploads require an active store session.");
      }

      const formData = new FormData();

      fileList.forEach((file) => {
        formData.append("files", file);
      });

      setIsSaving(true);

      try {
        const data = await apiClient.apiUpload(
          buildAttachmentUploadPath(roId, options?.uploadRequestId),
          formData
        );

        const savedRO = normalizeROSafe(data?.ro || null);

        if (savedRO?.id) {
          dispatch({
            type: ACTIONS.UPDATE_RO,
            payload: {
              id: roId,
              patch: savedRO,
              eventType: "",
              eventMeta: {},
            },
          });
        }

        setLastSavedAtISO(nowISO());

        return {
          attachments: Array.isArray(data?.attachments)
            ? data.attachments
            : [],
          ro: savedRO,
        };
      } catch (e) {
        setLastSyncError(friendlyApiError(e, String(e?.message || e)));
        await refreshStoreData();
        throw e;
      } finally {
        setIsSaving(false);
      }
    };

    const applyServerROSnapshot = (snapshot) => {
      const savedRO = normalizeROSafe(snapshot || null);
      if (!savedRO?.id) return null;

      dispatch({
        type: ACTIONS.UPDATE_RO,
        payload: {
          id: savedRO.id,
          patch: savedRO,
          eventType: "",
          eventMeta: {},
        },
      });

      return savedRO;
    };

    const deleteAttachment = async (attachmentId) => {
      assertCanMutate();

      const cleanId = safeString(attachmentId).trim();

      if (!cleanId) {
        throw new Error("Missing attachment id.");
      }

      if (!token || !authUser || authMode === "platform") {
        throw new Error("Attachment deletion requires an active store session.");
      }

      setIsSaving(true);

      try {
        const data = await apiClient.apiDelete(
          `/api/attachments/${encodeURIComponent(cleanId)}`
        );

        setLastSavedAtISO(nowISO());
        await refreshStoreData();

        return data;
      } catch (e) {
        setLastSyncError(friendlyApiError(e, String(e?.message || e)));
        await refreshStoreData();
        throw e;
      } finally {
        setIsSaving(false);
      }
    };

    const retractAttachment = async (attachmentId) => {
      assertCanMutate();

      const cleanId = safeString(attachmentId).trim();

      if (!cleanId) {
        throw new Error("Missing attachment id.");
      }

      if (!token || !authUser || authMode === "platform") {
        throw new Error("Attachment Undo Send requires an active store session.");
      }

      setIsSaving(true);

      try {
        const data = await apiClient.apiPost(
          `/api/attachments/${encodeURIComponent(cleanId)}/retract`,
          {}
        );

        const savedRO = normalizeROSafe(data?.ro || null);

        if (savedRO?.id) {
          dispatch({
            type: ACTIONS.UPDATE_RO,
            payload: {
              id: savedRO.id,
              patch: savedRO,
              eventType: "",
              eventMeta: {},
            },
          });
        }

        setLastSavedAtISO(nowISO());
        return data;
      } catch (e) {
        setLastSyncError(friendlyApiError(e, String(e?.message || e)));
        await refreshStoreData();
        throw e;
      } finally {
        setIsSaving(false);
      }
    };

    const setAttentionFlag = async (id, flag) => {
      const ro = ros.find((r) => r.id === id);
      const clean = safeString(flag).trim();
      if (!clean) return false;

      const flags = Array.from(new Set([...getAttentionFlags(ro), clean]));

      return updateRO(
        id,
        {
          attentionFlags: flags,
          timestamps: {
            ...(ro?.timestamps || {}),
            updatedAt: nowISO(),
          },
        },
        "ATTENTION_FLAG_ADDED",
        {
          roNumber: ro?.roNumber,
          flag: clean,
        }
      );
    };

    const removeAttentionFlag = async (id, flag) => {
      const ro = ros.find((r) => r.id === id);
      const clean = safeString(flag).trim().toLowerCase();

      const flags = getAttentionFlags(ro).filter(
        (f) => safeString(f).trim().toLowerCase() !== clean
      );

      return updateRO(
        id,
        {
          attentionFlags: flags,
          timestamps: {
            ...(ro?.timestamps || {}),
            updatedAt: nowISO(),
          },
        },
        "ATTENTION_FLAG_REMOVED",
        {
          roNumber: ro?.roNumber,
          flag,
        }
      );
    };

    const markVehicleGone = async (id, meta = {}) => {
      const ro = ros.find((r) => r.id === id);
      const stamp = nowISO();

      return updateRO(
        id,
        {
          status: roStatusValue("COMPLETED", "COMPLETED"),
          lifecycle: roLifecycleValue("COMPLETED", "COMPLETED"),

          customerPickupStatus: "VEHICLE_GONE",
          customer_pickup_status: "VEHICLE_GONE",

          advisorActionStatus: "VEHICLE_GONE",
          advisor_action_status: "VEHICLE_GONE",

          customerContacted: true,
          customer_contacted: true,

          vehicleGone: true,
          vehicle_gone: true,
          pickedUp: true,
          picked_up: true,
          pickedUpToday: true,

          pickedUpAtISO: stamp,
          picked_up_at_iso: stamp,
          vehicleGoneAtISO: stamp,
          vehicle_gone_at_iso: stamp,
          pickedUpAt: stamp,
          vehicleGoneAt: stamp,

          timestamps: {
            ...(ro?.timestamps || {}),
            pickedUpAt: stamp,
            vehicleGoneAt: stamp,
            updatedAt: stamp,
          },
        },
        "VEHICLE_GONE",
        {
          roNumber: ro?.roNumber,
          source: meta?.source || "advisor-log",
        }
      );
    };

    const reopenRO = async (id, meta = {}) => {
      const ro = ros.find((r) => r.id === id);

      return updateRO(
        id,
        {
          vehicleGone: false,
          vehicle_gone: false,
          pickedUp: false,
          picked_up: false,
          pickedUpToday: false,

          pickedUpAtISO: "",
          picked_up_at_iso: "",
          vehicleGoneAtISO: "",
          vehicle_gone_at_iso: "",
          pickedUpAt: "",
          vehicleGoneAt: "",

          customerPickupStatus: "",
          customer_pickup_status: "",
          advisorActionStatus: "",
          advisor_action_status: "",

          status:
            ro?.assignedTechName || ro?.assignedTechId
              ? roStatusValue("ASSIGNED", "ASSIGNED")
              : roStatusValue("AVAILABLE", "AVAILABLE"),
          lifecycle: roLifecycleValue("OPEN", "OPEN"),

          timestamps: {
            ...(ro?.timestamps || {}),
            reopenedAt: nowISO(),
            updatedAt: nowISO(),
          },
        },
        "RO_REOPENED",
        {
          roNumber: ro?.roNumber,
          source: meta?.source || "manual",
        }
      );
    };

    const updateLifecycle = async (id, lifecycle, meta = {}) => {
      const ro = ros.find((r) => r.id === id);
      const normalizedLifecycle = normalizeLifecycle(lifecycle);

      const aligned = alignLifecycleAndStatus({
        ...ro,
        lifecycle: normalizedLifecycle,
      });

      return updateRO(
        id,
        {
          lifecycle: aligned.lifecycle,
          status: aligned.status,
          timestamps: {
            ...(ro?.timestamps || {}),
            updatedAt: nowISO(),
          },
        },
        "LIFECYCLE_UPDATED",
        {
          roNumber: ro?.roNumber,
          lifecycle: aligned.lifecycle,
          status: aligned.status,
          source: meta?.source || "manual",
        }
      );
    };

    const updateStatus = async (id, status, meta = {}) => {
      const ro = ros.find((r) => r.id === id);
      const normalizedStatus = normalizeStatus(status);

      const aligned = alignLifecycleAndStatus({
        ...ro,
        status: normalizedStatus,
      });

      return updateRO(
        id,
        {
          lifecycle: aligned.lifecycle,
          status: aligned.status,
          timestamps: {
            ...(ro?.timestamps || {}),
            updatedAt: nowISO(),
          },
        },
        "STATUS_UPDATED",
        {
          roNumber: ro?.roNumber,
          lifecycle: aligned.lifecycle,
          status: aligned.status,
          source: meta?.source || "manual",
        }
      );
    };

    const captureWipSnapshot = (date = new Date()) => {
      const dateKey = isoDateKeyInTimeZone(date, storeTimeZone);
      const wip = ros.filter((r) => isWipRO(r));

      const snap = {
        id: genId("snap"),
        dateKey,
        capturedAtISO: nowISO(),
        timezone: storeTimeZone,

        wipCount: wip.length,
        waiterCount: wip.filter(
          (r) => normalizeTransportType(r.transportType) === TRANSPORT_TYPES.WAITER
        ).length,
        waitingOnPartsCount: wip.filter((r) => isWaitingForParts(r)).length,
        dispatchCount: wip.filter((r) => isWaitingForAssignment(r)).length,
        assignedCount: wip.filter((r) => isOpenAssignedRO(r)).length,
        inProgressCount: wip.filter((r) => isInProgressRO(r)).length,

        notes: "",
      };

      dispatch({ type: ACTIONS.UPSERT_WIP_SNAPSHOT, payload: snap });
      return snap;
    };
        const getWipSnapshotByDate = (dateKey) => {
      const key = safeString(dateKey).trim();
      if (!key) return null;
      const snap = snapshots?.[key];
      if (!snap || typeof snap !== "object") return null;
      return snap;
    };

    const checkWipSnapshot = (dateKey) => {
      const key =
        safeString(dateKey).trim() ||
        isoDateKeyInTimeZone(new Date(), storeTimeZone);
      const existing = getWipSnapshotByDate(key);
      return { dateKey: key, exists: !!existing, snapshot: existing || null };
    };

    const tryAutoCaptureToday = async () => {
      const key = isoDateKeyInTimeZone(new Date(), storeTimeZone);
      const existing = getWipSnapshotByDate(key);

      if (existing) {
        return {
          saved: false,
          skipped: true,
          reason: "EXISTS",
          snapshot: existing,
        };
      }

      return {
        saved: true,
        skipped: false,
        snapshot: captureWipSnapshot(new Date()),
      };
    };

    const setTransportType = async (id, transportType) => {
      const ro = ros.find((r) => r.id === id);
      const next = normalizeTransportType(transportType);

      return updateRO(
        id,
        {
          transportType: next,
          timestamps: {
            ...(ro?.timestamps || {}),
            updatedAt: nowISO(),
          },
        },
        "RO_TRANSPORT_CHANGED",
        {
          roNumber: ro?.roNumber,
          to: next,
        }
      );
    };

    const setWorkMix = async (id, workMix) => {
      const ro = ros.find((r) => r.id === id);
      const next = normalizeWorkMix(workMix);

      return updateRO(
        id,
        {
          workMix: next,
          timestamps: {
            ...(ro?.timestamps || {}),
            updatedAt: nowISO(),
          },
        },
        "RO_WORK_MIX_CHANGED",
        {
          roNumber: ro?.roNumber,
          to: next,
        }
      );
    };

    const setFlaggedHours = async (id, value) => {
      const ro = ros.find((r) => r.id === id);
      const hours = safeNumber(value);

      return updateRO(
        id,
        {
          flaggedHours: hours,
          hoursFlagged: hours,
          timestamps: {
            ...(ro?.timestamps || {}),
            updatedAt: nowISO(),
          },
        },
        "FLAGGED_HOURS_UPDATED",
        {
          roNumber: ro?.roNumber,
          flaggedHours: hours,
        }
      );
    };

    const resetDemoStore = () => {
      const blank = migrateStore(null);
      dispatch({ type: ACTIONS.SET_ALL, payload: blank });
      setLastSavedAtISO(nowISO());
      return true;
    };

    const getVehicleGoneAtISOForRO = (r) => {
      return (
        r?.vehicleGoneAtISO ||
        r?.vehicle_gone_at_iso ||
        r?.pickedUpAtISO ||
        r?.picked_up_at_iso ||
        r?.vehicleGoneAt ||
        r?.pickedUpAt ||
        r?.timestamps?.vehicleGoneAt ||
        r?.timestamps?.pickedUpAt ||
        r?.timestamps?.updatedAt ||
        r?.updatedAt ||
        ""
      );
    };

    const isVehicleGoneLikeRO = (r) => {
      const pickupStatus = actionKey(
        r?.customerPickupStatus || r?.customer_pickup_status
      );
      const advisorAction = actionKey(
        r?.advisorActionStatus || r?.advisor_action_status
      );

      return (
        pickupStatus === "VEHICLE_GONE" ||
        advisorAction === "VEHICLE_GONE" ||
        !!r?.vehicleGone ||
        !!r?.vehicle_gone ||
        !!r?.pickedUp ||
        !!r?.picked_up ||
        actionKey(r?.status) === "VEHICLE_GONE" ||
        actionKey(r?.lifecycle) === "VEHICLE_GONE"
      );
    };

    /*
      Canonical active WIP:
      Exclude vehicle-gone / picked-up records from the active operational bucket.
      Same-day vehicle-gone records are added back separately below for WIP Board
      same-day visibility.
    */
    const openROs = ros.filter((r) => {
      if (!isWipRO(r)) return false;
      if (isVehicleGoneLikeRO(r)) return false;
      return true;
    });

    const completedROs = ros.filter((r) => isCompletedRO(r));

    const todayCompletedROs = ros.filter((r) => {
      const completedAt =
        r?.completedAtISO ||
        r?.technicianCompletedAtISO ||
        r?.timestamps?.completedAt ||
        r?.timestamps?.technicianCompletedAt ||
        r?.updatedAt ||
        r?.timestamps?.updatedAt ||
        "";

      return isCompletedRO(r) && isTodayISO(completedAt, storeTimeZone);
    });

    const todaysVehicleGoneROs = ros.filter((r) => {
      const pickupStatus = actionKey(
        r?.customerPickupStatus || r?.customer_pickup_status
      );
      const advisorAction = actionKey(
        r?.advisorActionStatus || r?.advisor_action_status
      );

      const gone =
        pickupStatus === "VEHICLE_GONE" ||
        advisorAction === "VEHICLE_GONE" ||
        !!r?.vehicleGone ||
        !!r?.vehicle_gone ||
        !!r?.pickedUp ||
        !!r?.picked_up ||
        actionKey(r?.status) === "VEHICLE_GONE" ||
        actionKey(r?.lifecycle) === "VEHICLE_GONE";

      if (!gone) return false;

      const goneAt = getVehicleGoneAtISOForRO(r);

      return isTodayISO(goneAt, storeTimeZone);
    });

    const visibleWipROs = Array.from(
      new Map(
        [...openROs, ...todayCompletedROs, ...todaysVehicleGoneROs]
          .filter((r) => r?.id && !isArchived(r))
          .map((r) => [r.id, r])
      ).values()
    );

    /*
      Canonical operational buckets.

      Rule:
      - visibleWipROs is the WIP Board source of truth.
      - Pages should not recalculate active/current WIP from raw ros.
      - openROs is still the strict lifecycle WIP bucket.
      - visibleWipROs includes open WIP plus same-day completed/vehicle-gone
        visibility records, so the shop can see what happened today.
    */
    const currentWipROs = visibleWipROs;
    const visibleBoardROs = visibleWipROs;
    const managerDashboardROs = visibleWipROs;
    const foremanActiveROs = visibleWipROs;
    const advisorActiveROs = visibleWipROs;

    const availableROs = openROs.filter((r) => isWaitingForAssignment(r));
    const dispatchROs = availableROs;

    const assignedROs = openROs.filter((r) => isOpenAssignedRO(r));
    const inProgressROs = openROs.filter((r) => isInProgressRO(r));
    const waitingForPartsROs = openROs.filter((r) => isWaitingForParts(r));

    const wipROs = visibleWipROs;
    const completedLifecycleROs = completedROs;

    const openROsMissingTag = openROs.filter(
      (r) => !safeString(r.tagNumber).trim()
    );
    const wipROsMissingTag = visibleWipROs.filter(
      (r) => !safeString(r.tagNumber).trim()
    );

    const isOpenRO = (ro) => isWipRO(ro);

    const setStatus = updateStatus;
    const setLifecycle = updateLifecycle;
    const markCustomerContactedForPickup = markCustomerContacted;

    return {
      // state
      version: SCHEMA_VERSION,
      isLoading,
      isSyncing,
      isSaving,
      lastSyncError,
      lastSavedAtISO,
      isAuthed: !!token && !!authUser,
      isImpersonating: !!auth?.isImpersonating,

      // auth / user
      currentUser,
      setCurrentUser,

      // settings / staff
      settings,
      updateSettings,
      staffDirectory,
      techs,
      createStaff,
      updateStaff,
      refreshStaff,

      // raw data
      ros,
      repairOrders: ros,
      allROs: ros,
      snapshots,

      // buckets
      openROs,
      wipROs,
      visibleWipROs,
      currentWipROs,
      visibleBoardROs,
      managerDashboardROs,
      foremanActiveROs,
      advisorActiveROs,
      completedROs,
      completedLifecycleROs,
      availableROs,
      dispatchROs,
      assignedROs,
      inProgressROs,
      waitingForPartsROs,
      openROsMissingTag,
      wipROsMissingTag,
      isOpenRO,

      // CRUD
      addRO,
      updateRO,
      deleteRO,
      archiveRO,
      refreshStoreData,
      refreshROsOnly,

      // assignment / RO operations
      assignTech,
      unassignTech,
      startWork,
      stopWork,
      endWork,
      completeWork,
      completeRO,
      reopenRO,

      // status / lifecycle / fields
      updateStatus,
      setStatus,
      updateLifecycle,
      setLifecycle,
      setTransportType,
      setWorkMix,
      setFlaggedHours,
      validateTagNumber,

      // parts
      requestParts,
      requestPartsAction,
      requestPartsReview,
      reopenPartsReview,
      updatePartsAction,
      updatePartsStatus,
      setPartsActionStatus,
      setPartsStatus,
      savePartsEta,
      setPartsEta,
      updatePartsEta,

      // advisor pickup workflow
      markCustomerContacted,
      markCustomerContactedForPickup,
      setCustomerContactStatus,
      clearCustomerContactStatus,
      markVehicleGone,

      // flags
      listAttachmentsForRO,
      uploadAttachmentsForRO,
      getAttachmentSignedUrl,
      deleteAttachment,
      retractAttachment,
      applyServerROSnapshot,
      setAttentionFlag,
      removeAttentionFlag,

      // snapshots
      captureWipSnapshot,
      getWipSnapshotByDate,
      checkWipSnapshot,
      tryAutoCaptureToday,

      // constants/helpers exposed for pages
      TRANSPORT_TYPES,
      WORK_MIX,
      RO_STATUS,
      RO_LIFECYCLE,
      PARTS_STATUS,
      PARTS_REQUEST_TYPE,
      PARTS_ACTION_STATUS,

      isWipRO,
      isCompletedRO,
      isArchived,
      isWaitingForParts,
      isWaitingForAssignment,
      isOpenAssignedRO,
      isInProgressRO,
      getAttentionFlags,
      getLifecycle,

      // development helper
      resetDemoStore,
    };
  }, [
    state,
    auth,
    authUser,
    token,
    authMode,
    effectiveStoreId,
    apiClient,
    isLoading,
    isSyncing,
    isSaving,
    lastSyncError,
    lastSavedAtISO,
  ]);

  useEffect(() => {
    if (!token || !authUser || authMode === "platform") return undefined;
    if (!effectiveStoreId) return undefined;

    let cancelled = false;
    let intervalId = null;

    const poll = async () => {
      if (cancelled) return;
      if (document.visibilityState === "hidden") return;
      if (isSaving || isSyncing) return;

      try {
        if (typeof api.refreshROsOnly === "function") {
          await api.refreshROsOnly();
        } else {
          await api.refreshStoreData();
        }
      } catch {
        // refresh already records a readable sync error
      }
    };

    intervalId = window.setInterval(poll, 5000);

    function onVisible() {
      if (document.visibilityState === "visible") {
        poll();
      }
    }

    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [
    token,
    authUser,
    authMode,
    effectiveStoreId,
    isSaving,
    isSyncing,
    api,
  ]);

  return <ROContext.Provider value={api}>{children}</ROContext.Provider>;
}

export function useROStore() {
  const ctx = useContext(ROContext);
  if (!ctx) {
    throw new Error("useROStore must be used inside ROProvider");
  }
  return ctx;
}

export default ROContext;
